import { ChartColumn, ReceiptText, Route } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { BrandMark } from './Brand';
import { ThemeCycleButton } from './ThemeSwitch';

/** Split screen for sign-in / first setup: brand panel (large screens) + form. */
export function AuthShell({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  const { t } = useTranslation();
  const points = [
    { icon: Route, key: 'trace' },
    { icon: ChartColumn, key: 'copper' },
    { icon: ReceiptText, key: 'invoice' },
  ] as const;
  return (
    <main className="grid min-h-screen bg-surface lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <section className="relative hidden overflow-hidden bg-night p-12 text-white lg:flex lg:flex-col">
        {/* Concentric coil rings */}
        <svg
          aria-hidden
          viewBox="0 0 600 600"
          className="pointer-events-none absolute -bottom-40 -end-40 size-[640px] text-copper-500/25"
        >
          {Array.from({ length: 9 }, (_, i) => (
            <circle
              key={i}
              cx="300"
              cy="300"
              r={40 + i * 30}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.2"
            />
          ))}
        </svg>
        <div className="flex items-center gap-3">
          <BrandMark className="size-10" />
          <div className="leading-tight">
            <div className="text-lg font-semibold">{t('app.short')}</div>
            <div className="text-sm text-white/55">{t('app.tagline')}</div>
          </div>
        </div>
        <div className="relative mt-auto max-w-md">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">
            {t('login.headline')}
          </h2>
          <ul className="mt-8 space-y-4">
            {points.map(({ icon: Icon, key }) => (
              <li key={key} className="flex items-start gap-3">
                <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-white/10 text-copper-300">
                  <Icon aria-hidden className="size-4" />
                </span>
                <span className="pt-1 text-sm text-white/75">{t(`login.points.${key}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <section className="flex flex-col px-6 py-8 sm:px-12">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 lg:invisible">
            <BrandMark className="size-8" />
            <span className="text-[15px] font-semibold">{t('app.short')}</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeCycleButton className="grid size-8 place-items-center rounded-lg text-stone-500 ring-1 ring-inset ring-stone-300 transition-colors hover:bg-stone-100 hover:text-stone-900" />
            {aside}
          </div>
        </div>
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-10">
          {children}
        </div>
      </section>
    </main>
  );
}
